class TrackingBase {
    constructor() {
        this.initializeDataLayer();
    }

    /**
     * Initialize Google Tag Manager dataLayer with current or new array if null
     */
    initializeDataLayer() {
        // window.dataLayer = window.dataLayer || [];
        return true;
    }
}

class TrackAuth extends TrackingBase {
    /**
     * Track login event with username and status
     * @param {string} username - The username of the user
     * @param {string} status - The status of the login attempt (e.g., "success", "incomplete", "fail")
     */
    login(username, status) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "login",
            method: "typed",
            status: status,
            username: username
        });
    }

    /**
     * Track logout event with status
     * @param {string} status - The status of the logout attempt
     */
    logout(status) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "logout",
            name: "logout",
            status: status,
            location: window.location.href,
        });
    }
}

class TrackPurchaseJourney extends TrackingBase {
    /**
     * Reset ecommerce object in data layer to clear previous values
     */
    resetEcommerce() { window.dataLayer.push({ ecommerce: null }); }

    /**
     * Track event when item list is viewed
     * @param {'search'|'clearance'|'navigation'} source - The source of the item list view
     * @param {{item_list_id:string, item_list_name:string, currency:string, value:number}} model - The model containing item list details
     */
    viewItemList(source, model) {
        this.initializeDataLayer();
        this.resetEcommerce();
        window.dataLayer.push({
            event: "view_item_list_trigger",
            source: source,
            ecommerce: {
                item_list_id: model?.item_list_id,
                item_list_name: model?.item_list_name,
                currency: model?.currency,
                value: model?.value
            }
        });
    }

    /**
     * Track event when an item is viewed
     * @param {{source:'search'|'clearance'|'navigation',currency:string, value:number, items:Array}} model - The model containing item details
     */
    viewItem(model) {
        this.initializeDataLayer();
        this.resetEcommerce();
        window.dataLayer.push({
            event: "view_item_trigger",
            source: model.source,
            ecommerce: {
                currency: model?.currency,
                value: model?.value,
                items: model.items
            }
        });
    }

    /**
     * Track event when an item is added to the cart
     * @param {{currency:string, value:number, items:Array, source?:'Construction Configurator'|'LED Configurator'}} model - The model containing item details
     */
    addToCart(model) {
        this.initializeDataLayer();
        this.resetEcommerce();
        window.dataLayer.push({
            event: "add_to_cart_trigger",
            ecommerce: {
                currency: model?.currency,
                value: model?.value,
                items: model.items
            },
            ...(model.source && { source: model.source })
        });
    }

    /**
     * Track event when the cart is viewed
     * @param {{currency:string, value:number, items:Array}} model - The model containing cart details
     */
    viewCart(model) {
        this.initializeDataLayer();
        this.resetEcommerce();
        window.dataLayer.push({
            event: "view_cart_trigger",
            ecommerce: {
                currency: model?.currency,
                value: model?.value,
                items: model.items
            }
        });
    }

    /**
     * Track event when an item is removed from the cart
     * @param {{currency:string, value:number, items:Array}} model - The model containing item details
     */
    removeFromCart(model) {
        this.initializeDataLayer();
        this.resetEcommerce();
        window.dataLayer.push({
            event: "remove_from_cart_trigger",
            ecommerce: {
                currency: model?.currency,
                value: model?.value,
                items: model.items
            }
        });
    }

    /**
     * Track event when a purchase is made
     * @param {{transaction_id:any, currency:string, value:number, items:Array}} model - The model containing purchase details
     */
    purchase(model) {
        this.initializeDataLayer();
        this.resetEcommerce();
        window.dataLayer.push({
            event: "purchase_trigger",
            ecommerce: {
                transaction_id: model.transaction_id,
                currency: model?.currency,
                value: model?.value,
                items: model.items
            }
        });
    }

    /**
     * Track event when shipping information is added
     * @param {{currency:string, value:number, save:string, items:Array}} model - The model containing shipping details
     */
    addShippingInfo(model) {
        this.initializeDataLayer();
        this.resetEcommerce();
        window.dataLayer.push({
            event: "add_shipping_info_trigger",
            ecommerce: {
                currency: model?.currency,
                value: model?.value,
                save: model?.save,
                items: model?.items
            }
        });
    }
}

class TrackOrder extends TrackingBase {
    /**
     * Track order search event
     * @param {{search_term:string, input_query:string}} model - The model containing search details
     */
    trackOrder(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "track_order_trigger",
            search_term: model.search_term,
            input_query: model.input_query,
            location: window.location.href
        });
    }

    /**
     * Track order status event
     * @param {{search_term:string, input_query:string, status:'success'|'fail'|'incomplete', name:string}} model - The model containing status details
     */
    statusTrackOrder(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "status_track_order_trigger",
            search_term: model.search_term,
            input_query: model.input_query,
            status: model.status,
            name: model.name,
            location: window.location.href,
        });
    }
}

class TrackCompare extends TrackingBase {
    /**
     * Track start compare event
     * @param {{search_term:string, input_query:string, status:'success'|'fail'|'incomplete'}} model - The model containing compare details
     */
    startCompare(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "start_compare_trigger",
            search_term: model.search_term,
            input_query: model.input_query,
            status: model.status,
            location: window.location.href
        });
    }

    /**
     * Track compare results event
     * @param {{name:string,search_term:string, input_query:string, category:string, quantity:number, step1:any, step2:any}} model - The model containing compare results
     */
    resultCompare(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "results_compare_trigger",
            name: model.name,
            search_term: model.search_term,
            input_query: model.input_query,
            category: model.category,
            quantity: model.quantity,
            step1: model.step1,
            step2: model.step2,
            location: window.location.href
        });
    }
}

class TrackConfigurator extends TrackingBase {
    /**
     * Track tab configurator event
     * @param {{name:string}} model - The model containing configurator details
     */
    tabConfigurator(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "tab_configurator_trigger",
            name: model?.name,
            location: window.location.href,
        });
    }

    /**
     * Track configurator cta event
     * @param {{name:string}} model - The model containing configurator details
     */
    configuratorCTAClick(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "configurator_cta_click_trigger",
            search_term: model?.search_term,
            name: model?.name,
            location: window.location.href,
        });
    }
}

class TrackDownload extends TrackingBase {
    /**
     * Triggers the download cart event.
     * @param {{currency: string, value: number, items: Array}} model - The model containing cart details.
     */
    downloadCart(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "download_cart_trigger",
            currency: model.currency,
            value: model.value,
            items: model.items,
            location: window.location.href,
        });
    }

    /**
     * Triggers the submit purchase order event.
     * @param {{currency: string, value: number, items: Array}} model - The model containing purchase order details.
     */
    submitPo(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "submit_po_trigger",
            currency: model.currency,
            value: model.value,
            items: model.items,
            location: window.location.href,
        });
    }

    /**
     * Triggers the download assets event.
     * @param {{name: string, search_term: string, input_query: string, category: string}} model - The model containing asset details.
     */
    downloadAssets(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "download_assets_trigger",
            name: model.name,
            search_term: model.search_term,
            input_query: model.input_query,
            category: model.category,
            location: window.location.href,
        });
    }
}

class Track {
    constructor() {
        this.Auth = new TrackAuth();
        this.Purchase = new TrackPurchaseJourney();
        this.Order = new TrackOrder();
        this.Compare = new TrackCompare();
        this.Configurator = new TrackConfigurator();
        this.Downloads = new TrackDownload();
    }

    /**
     * Initialize Google Tag Manager dataLayer with current or new array if null
     */
    initializeDataLayer() {
        // window.dataLayer = window.dataLayer || [];
        return true;
    }

    /**
     * Track navigation event when user navigates between pages
     * @param {string} name - Any given name or page path name (e.g., /pagename)
     * @param {string} pagePath - Page URL or href
     */
    navigation(name, pagePath) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "navigation",
            name: name || window.location.pathname,
            location: pagePath || window.location.href,
        });
    }

    /**
     * Track search event when user performs a search
     * @param {{search_term:string, input_query:string}} model - The model containing search details
     */
    search(model) {
        this.initializeDataLayer();
        window.dataLayer.push({
            event: "search",
            search_term: model.search_term,
            input_query: model.input_query,
            location: window.location.href
        });
    }
}

// Singleton pattern to ensure only one instance of Track is created
class GoogleAnalyticsSingleton {
    constructor() {
        if (!GoogleAnalyticsSingleton.instance) {
            GoogleAnalyticsSingleton.instance = new Track();
        }
    }

    /**
     * Get singleton instance of Track
     * @returns {Track}
     */
    getInstance() {
        return GoogleAnalyticsSingleton.instance;
    }
}

export const GoogleAnalytics = { Track: new GoogleAnalyticsSingleton().getInstance() };